import { useEffect, useState } from "react";
import api from "../config/api";

/**
 * Custom hook for fetching and managing guest images
 * 
 * This hook handles all operations related to guest images including:
 * - Fetching images from the API with filtering options
 * - Managing loading, error, and success states
 * - Storing event details alongside images
 * - Providing utilities for state management
 * 
 * @param {Object} params - Hook parameters
 * @param {string|number} params.eventId - The event ID to fetch images for
 * 
 * @returns {Object} Hook state and functions
 * @returns {Array} returns.images - Array of guest image objects
 * @returns {Object} returns.eventDetails - Event information details
 * @returns {boolean} returns.loading - Loading state for API operations
 * @returns {string|null} returns.error - Error message if any
 * @returns {Function} returns.fetchGuestImages - Function to fetch images
 * @returns {Function} returns.resetGuestImages - Function to reset all state
 * @returns {Function} returns.setImages - Function to manually update images
 */
const useGuestImages = ({ eventId }) => {
  // State management for images and event data
  const [images, setImages] = useState([]); // Array of guest images
  const [eventDetails, setEventDetails] = useState({}); // Event information
  const [loading, setLoading] = useState(false); // API loading state
  const [error, setError] = useState(null); // Error state
  const [pagination, setPagination] = useState({
    max_page: 1,
    next_page: null,
    previous_page: null,
    total_count: 0
  }); // Pagination state
  const [currentPage, setCurrentPage] = useState(1); // Current page number

  /**
   * Fetches guest images from the API
   * 
   * Makes a GET request to retrieve images for a specific event.
   * Supports filtering by uploader, pagination, and handles all error cases.
   * 
   * @param {string|number} eventId - Event ID to fetch images for
   * @param {string} uploadedBy - Filter by uploader ('all' for everyone, 'me' for current user, or specific user ID)
   * @param {number} page - Page number to fetch (default: 1)
   * @param {boolean} append - Whether to append to existing images or replace (default: false)
   * @returns {Promise<Object|null>} - API response data or null if error occurs
   */
  const fetchGuestImages = async (eventId, uploadedBy = "all", page = 1, append = false) => {
    // Validate required parameter
    if (!eventId) {
      setError("Event ID is required");
      return null;
    }

    // Set loading state and clear previous errors
    setLoading(true);
    setError(null);

    try {
      // Make API request with query parameters
      const response = await api.get(
        `/event-guest-images/list/${eventId}`,
        {
          params: {
            uploaded_by: uploadedBy, // Filter images by uploader
            page: page, // Add page parameter
            item: 10,
            // paginate: 1
          },
        }
      );

      // Update state with response data
      const responseData = response.data;
      
      if (responseData.status === "SUCCESS") {
        const { data, pagination: paginationData } = responseData;
        
        // Update images based on append flag
        if (append) {
          setImages(prev => [...prev, ...(data.images || [])]);
        } else {
          setImages(data.images || []); // Ensure array fallback
        }
        
        setEventDetails(data || {}); // Store event details
        setPagination(paginationData || {
          max_page: 1,
          next_page: null,
          previous_page: null,
          total_count: 0
        });
        setCurrentPage(page);
      } else {
        throw new Error(responseData.message || "Failed to fetch images");
      }
      
      setLoading(false);
      return responseData;
    } catch (err) {
      // Handle API errors with descriptive messages
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch guest images";
      setError(errorMessage);
      setLoading(false);
      
      return null;
    }
  };

  /**
   * Effect hook to automatically fetch images when eventId changes
   * Fetches only current user's images by default ("me" filter)
   */
  useEffect(() => {
    if (eventId) {
      fetchGuestImages(eventId, "me"); // Auto-fetch current user's images
    }
  }, [eventId]); // Dependency: re-run when eventId changes

  /**
   * Fetches next page of images
   * @returns {Promise<Object|null>} - API response data or null if error
   */
  const fetchNextPage = async () => {
    if (pagination.next_page) {
      return await fetchGuestImages(eventId, "me", pagination.next_page, true);
    }
    return null;
  };

  /**
   * Fetches previous page of images
   * @returns {Promise<Object|null>} - API response data or null if error
   */
  const fetchPreviousPage = async () => {
    if (pagination.previous_page) {
      return await fetchGuestImages(eventId, "me", pagination.previous_page, true);
    }
    return null;
  };

  /**
   * Fetches specific page of images
   * @param {number} pageNumber - Page number to fetch
   * @returns {Promise<Object|null>} - API response data or null if error
   */
  const fetchPage = async (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.max_page) {
      return await fetchGuestImages(eventId, "me", pageNumber, false);
    }
    return null;
  };

  /**
   * Resets the hook state to initial values
   * Useful for cleanup or when switching events
   */
  const resetGuestImages = () => {
    setImages([]); // Clear images array
    setEventDetails({}); // Clear event details
    setLoading(false); // Reset loading state
    setError(null); // Clear any errors
    setPagination({
      max_page: 1,
      next_page: null,
      previous_page: null,
      total_count: 0
    });
    setCurrentPage(1);
  };

  // Return all state and functions for component consumption
  return {
    images: images || [], // Ensure array is always returned
    loading,
    error,
    fetchGuestImages,
    fetchNextPage,
    fetchPreviousPage,
    fetchPage,
    resetGuestImages,
    eventDetails,
    setImages, // Allow manual image updates (useful for delete operations)
    pagination,
    currentPage,
    hasNextPage: !!pagination.next_page,
    hasPreviousPage: !!pagination.previous_page,
    totalPages: pagination.max_page,
    totalCount: pagination.total_count
  };
};

export default useGuestImages;
